'use client';

import * as React from 'react';
import {
  ComposedChart,
  Bar,
  BarStack,
  Cell,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
} from 'recharts';

import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from '@/hen-ui/shadcn/chart';

import { cn } from '@/hen-ui/utils';

import { ChartLegend } from '../ChartLegend';
import {
  getSafeChartProps,
  useMissingChartConfigWarning,
  buildSafeSeriesKeyMap,
  renderReferenceLines,
  renderChartTick,
  deriveValueAxisDefaults,
  validateTooltipMode,
  renderTooltipFooter,
  usePinnedTooltip,
  useInteractiveLegend,
  X_AXIS_LABEL_MARGIN_PX,
  sanitizeTicks,
} from '../chart-utils';
import {
  withSeriesColorScaleDefaults,
  resolveDivergingArmColor,
} from '../chart-palette';
import {
  shouldRotateXAxisLabels,
  getDynamicXAxisHeight,
  getXAxisRightMargin,
} from '@/hen-ui/utils/chart';
import type {
  ChartDataPoint,
  ReferenceLineConfig,
  SeriesColorConfig,
  ChartTooltipMode,
  ChartTooltipFooter,
  ColorScale,
  SequentialPalette,
  DivergingPalette,
} from '../chart-types';
import {
  resolveTooltipFormat,
  resolveValueFormat,
  type TooltipFormat,
  type ValueFormat,
} from '../number-format';
import {
  evaluateColorRules,
  resolveSeriesBaseColor,
  buildTooltipIndicatorColorResolver,
  withSemanticLegendColors,
} from '../chart-color-rules';

export interface ComboChartProps {
  /** Pre-structured data rows; keys match xAxisKey and the entries in barSeries / lineSeries */
  data: ChartDataPoint[];
  /** Chart config: maps each series key to label + color (shadcn ChartConfig) */
  config: ChartConfig;
  /** Series keys rendered as bars on the left Y-axis */
  barSeries: string[];
  /** Series keys rendered as lines on the right Y-axis */
  lineSeries: string[];
  /** Key in each data row used for the X-axis category */
  xAxisKey: string;
  /** Label displayed alongside the X-axis */
  xAxisLabel?: string;
  /** Label displayed alongside the left Y-axis (bars) */
  leftYAxisLabel?: string;
  /** Label displayed alongside the right Y-axis (lines) */
  rightYAxisLabel?: string;
  /** Fixed range for the left (bar) Y-axis, e.g. [0, 5000] */
  leftYAxisDomain?: [number, number];
  /** Fixed range for the right (line) Y-axis, e.g. [0, 100] */
  rightYAxisDomain?: [number, number];
  /** Specific tick values on the left (bar) Y-axis, e.g. [0, 1000, 2000, 3000] */
  leftYAxisTicks?: number[];
  /** Specific tick values on the right (line) Y-axis, e.g. [0, 25, 50, 75, 100] */
  rightYAxisTicks?: number[];
  /** Formatter for left (bar) Y-axis tick labels */
  leftYAxisFormat?: ValueFormat;
  /** Formatter for right (line) Y-axis tick labels */
  rightYAxisFormat?: ValueFormat;
  /**
   * Formatter for the numeric value rendered in the tooltip. Receives the
   * series name so dual-axis charts can format bar series vs line series
   * differently. When omitted, falls back to `leftYAxisFormat` /
   * `rightYAxisFormat` based on which axis the series belongs to.
   */
  tooltipValueFormat?: (value: number, name: string | number) => string;
  /** Bar grouping mode (default "grouped") */
  mode?: 'grouped' | 'stacked';
  /**
   * Sort direction for data rows before rendering.
   * - `'desc'` — highest value first (ranking view)
   * - `'asc'`  — lowest value first
   * - `'none'` — preserve the original array order (default)
   *
   * When `sortBySeries` is provided, rows are ranked by that series key's
   * value. When omitted, rows are ranked by the **sum** of all bar + line
   * series values per row so the overall combined magnitude drives the order.
   */
  sortBy?: 'asc' | 'desc' | 'none';
  /**
   * Series key to use as the sort criterion when `sortBy` is `'asc'` or
   * `'desc'`. Must be one of the keys present in `barSeries[]` or
   * `lineSeries[]`. When omitted (default), the sort falls back to the
   * **sum** of all bar + line series values for each row.
   *
   * Invalid keys (not in `barSeries[]` ∪ `lineSeries[]`) are ignored and
   * the sort falls back to the sum-of-series strategy; a one-time
   * `console.warn` is emitted in development to surface the typo.
   *
   * @example sortBySeries="revenue" // sort by the revenue bar values
   */
  sortBySeries?: string;
  /**
   * Chart height in px (default 300).
   *
   * - With `fillContainer={false}` (default): fixed at exactly this many pixels.
   * - With `fillContainer={true}`: acts as the **minimum** height.
   */
  height?: number;
  /** When true, the chart fills its parent container's height. */
  fillContainer?: boolean;
  /** Maximum width of a single bar in px (default 40) */
  maxBarSize?: number;
  /** Border radius for bar corners in px (default 4) */
  barRadius?: number;
  /** Show legend below chart (default true) */
  showLegend?: boolean;
  /**
   * When `true`, each legend item is an interactive toggle button.
   * Clicking (or pressing Enter / Space) hides / shows that series.
   * Cannot hide the last visible series.
   *
   * Has no effect when `showLegend` is `false` — setting both is a
   * misconfiguration and emits a one-time `console.warn`.
   *
   * **Axis stability:** Both axis domains remain derived from all declared
   * series (not just visible ones) so axes do not rescale on toggle.
   *
   * @default false
   */
  clickableLegend?: boolean;
  /** Show dots on data points for line series (default true) */
  showDots?: boolean;
  /** Curve interpolation type for line series (default "monotone") */
  curveType?: 'monotone' | 'linear';
  /** Connect across null/missing values on line series (default false) */
  connectNulls?: boolean;
  /** Optional reference/target lines drawn on top of the chart */
  referenceLines?: ReferenceLineConfig[];
  /**
   * Override the default character-count limit (30) used when truncating
   * x-axis labels. Values below 4 are ignored (falls back to the library
   * default). Useful for charts with long category names.
   */
  xAxisLabelMaxLength?: number;
  /** When true, suppresses the tooltip header label; series rows are unaffected */
  hideLabel?: boolean;
  /**
   * Controls when the data-point tooltip is shown.
   * - `'hover'`    — standard hover tooltip (default).
   * - `'pinned'`   — tooltip appears on click and stays until the user
   *                  clicks elsewhere on the chart or presses Escape.
   * - `'disabled'` — tooltip is suppressed entirely.
   */
  tooltipMode?: ChartTooltipMode;
  /**
   * Row-aware value formatter for the tooltip. Receives the numeric value,
   * the series key, and the full raw data row. Takes precedence over
   * `tooltipValueFormat` when both are provided.
   */
  tooltipFormatter?: TooltipFormat;
  /**
   * Methodology copy rendered as a footer note inside the tooltip bubble.
   * - Plain `string`    — rendered verbatim.
   * - `{ md: string }` — parsed for `**bold**`, `*italic*`, `[text](url)`.
   */
  tooltipContent?: ChartTooltipFooter;
  /**
   * Per-series color configuration for conditional coloring. Maps series
   * key to its `SeriesColorConfig` (semantic color and/or color rules).
   * When provided, bar series render per-point `<Cell>` fills and line
   * series use custom per-dot fills + a semantic stroke override.
   */
  seriesColorConfigs?: Record<string, SeriesColorConfig>;
  /**
   * Color-scale mode for the per-series default color (default
   * `'categorical'`). `'sequential'` spreads a light→dark gradient across the
   * visible bar + line series; `'diverging'` colors each bar/dot by the data
   * point's sign. Semantic rules (`seriesColorConfigs`) and explicit
   * `config[key].color` still win. Omitting this renders byte-identically to
   * categorical.
   */
  colorScale?: ColorScale;
  /** Sequential palette family when `colorScale='sequential'` (default `'blues'`). */
  sequentialPalette?: SequentialPalette;
  /** Diverging palette family when `colorScale='diverging'` (default `'blueOrange'`). */
  divergingPalette?: DivergingPalette;
}

const EMPTY_FALLBACK_TEXT = 'No data available';
const NO_SERIES_TEXT = 'No series configured';

const VALUE_AXIS_DEFAULT_DOMAIN: [number, 'auto'] = [0, 'auto'];

/**
 * Pure sort helper for ComboChart rows. Exported for unit testing.
 *
 * - `sortBy='none'` returns the input array reference unchanged.
 * - `sortBySeries` (when provided **and** present in `allSeries`) sorts by
 *   that key's per-row numeric value (non-numeric → 0).
 * - `sortBySeries` provided but NOT in `allSeries` is treated as invalid:
 *   the helper falls back to the sum-of-series strategy. (The component
 *   emits a dev-only `console.warn` separately so consumers notice the
 *   typo; the pure helper stays silent and side-effect-free.)
 * - When no valid `sortBySeries` is set, rows are ranked by the **sum** of
 *   all `allSeries` values per row so combined magnitude drives the order.
 * - Always returns a new array when sorting (never mutates the input).
 */
export const sortComboChartData = (
  data: ChartDataPoint[],
  allSeries: string[],
  sortBy: 'asc' | 'desc' | 'none',
  sortBySeries?: string,
): ChartDataPoint[] => {
  if (sortBy === 'none') return data;

  // Validate sortBySeries: only honor it when it's actually one of the
  // declared series. An invalid key would make every sortKey 0 (a stable
  // no-op sort with surprising semantics), so we fall back to the
  // sum-of-series ranking instead.
  const effectiveSortBySeries =
    sortBySeries && allSeries.includes(sortBySeries) ? sortBySeries : undefined;

  // Decorate: attach a sortKey to each row
  const decorated = data.map((row) => {
    let sortKey: number;
    if (effectiveSortBySeries) {
      sortKey =
        typeof row[effectiveSortBySeries] === 'number'
          ? (row[effectiveSortBySeries] as number)
          : 0;
    } else {
      sortKey = allSeries.reduce(
        (sum, key) =>
          sum + (typeof row[key] === 'number' ? (row[key] as number) : 0),
        0,
      );
    }
    return { row, sortKey };
  });

  // Sort by the precomputed sortKey
  decorated.sort((a, b) =>
    sortBy === 'desc' ? b.sortKey - a.sortKey : a.sortKey - b.sortKey,
  );

  // Undecorate: return the original rows in sorted order
  return decorated.map((d) => d.row);
};

// Module-scoped warning guards to ensure one-time warnings per app session (even in StrictMode)
const warnedDuplicates = new Set<string>();
const warnedDomain = new Set<string>();
const warnedMissing = new Set<string>();
const warnedSortBySeries = new Set<string>();

/**
 * Test-only helper. Clears the module-scoped warning de-dupe Sets so each
 * test starts from a clean slate. Not part of the public API — re-exported
 * from the component file (NOT from `index.ts`) so tests can wire it into
 * `beforeEach` without it leaking into consumer apps via the package
 * barrel.
 */
export const __resetComboChartWarnings = (): void => {
  warnedDuplicates.clear();
  warnedDomain.clear();
  warnedMissing.clear();
  warnedSortBySeries.clear();
};

export const ComboChart = React.forwardRef<HTMLDivElement, ComboChartProps>(
  (
    {
      data: rawData,
      config,
      barSeries: rawBarSeries,
      lineSeries: rawLineSeries,
      xAxisKey,
      xAxisLabel,
      leftYAxisLabel,
      rightYAxisLabel,
      leftYAxisDomain: rawLeftDomain,
      rightYAxisDomain: rawRightDomain,
      leftYAxisTicks: rawLeftTicks,
      rightYAxisTicks: rawRightTicks,
      leftYAxisFormat: rawLeftYAxisFormat,
      rightYAxisFormat: rawRightYAxisFormat,
      tooltipValueFormat,
      mode = 'grouped',
      sortBy = 'none',
      sortBySeries,
      height = 300,
      fillContainer = false,
      maxBarSize = 40,
      barRadius = 4,
      showLegend = true,
      clickableLegend = false,
      showDots = true,
      curveType = 'monotone',
      connectNulls = false,
      referenceLines,
      xAxisLabelMaxLength,
      hideLabel,
      tooltipMode,
      tooltipFormatter: rawTooltipFormatter,
      tooltipContent,
      seriesColorConfigs,
      colorScale = 'categorical',
      sequentialPalette,
      divergingPalette,
    },
    ref,
  ) => {
    const leftYAxisFormat = React.useMemo(
      () => resolveValueFormat(rawLeftYAxisFormat),
      [rawLeftYAxisFormat],
    );
    const rightYAxisFormat = React.useMemo(
      () => resolveValueFormat(rawRightYAxisFormat),
      [rawRightYAxisFormat],
    );
    const tooltipFormatter = React.useMemo(
      () => resolveTooltipFormat(rawTooltipFormatter, leftYAxisFormat),
      [rawTooltipFormatter, leftYAxisFormat],
    );

    // Deduplicate: if a key appears in both barSeries and lineSeries, bar wins.
    const { barSeries, lineSeries } = React.useMemo(() => {
      const barSet = new Set(rawBarSeries);
      const dedupedLine = rawLineSeries.filter((key) => {
        if (barSet.has(key)) {
          if (
            process.env.NODE_ENV !== 'production' &&
            !warnedDuplicates.has(key)
          ) {
            warnedDuplicates.add(key);
            console.warn(
              `[hen-ui/ComboChart] Key "${key}" appears in both barSeries and lineSeries. Bar wins — the key is rendered as a bar, not a line.`,
            );
          }
          return false;
        }
        return true;
      });
      return { barSeries: rawBarSeries, lineSeries: dedupedLine };
    }, [rawBarSeries, rawLineSeries]);

    const allSeries = React.useMemo(
      () => [...barSeries, ...lineSeries],
      [barSeries, lineSeries],
    );

    const {
      data,
      yAxisDomain: leftDomainSafe,
      yAxisTicks: leftTicksRaw,
    } = getSafeChartProps({
      data: rawData,
      yAxisDomain: rawLeftDomain,
      yAxisTicks: rawLeftTicks,
    });
    // Run BOTH axes through `sanitizeTicks` so consumers get symmetric
    // handling — `getSafeChartProps` only defensively clones; it does NOT
    // drop NaN / dedupe / sort.
    //
    // The `chartName` passed in becomes the `[hen-ui/<chartName>]` prefix
    // on per-entry warns. Encode the specific prop (`leftYAxisTicks` /
    // `rightYAxisTicks`) so the warning is actionable — telling the
    // consumer to inspect `ComboChart.leftYAxisTicks`, not the generic
    // (and non-existent on ComboChart) `yAxisTicks` prop. Distinct
    // chartName values also keep the per-axis warnOnce keys independent
    // so a NaN at index 2 on the left axis doesn't silence the same
    // index on the right axis.
    const leftTicksSafe = React.useMemo(
      () => sanitizeTicks(leftTicksRaw, 'ComboChart.leftYAxisTicks'),
      [leftTicksRaw],
    );
    const rightDomainSafe = React.useMemo(
      () =>
        rawRightDomain ? ([...rawRightDomain] as [number, number]) : undefined,
      [rawRightDomain],
    );
    const rightTicksSafe = React.useMemo(
      () => sanitizeTicks(rawRightTicks, 'ComboChart.rightYAxisTicks'),
      [rawRightTicks],
    );
    // Dev-only guard: warn if sortBySeries is set to a key not in allSeries.
    React.useEffect(() => {
      if (
        process.env.NODE_ENV !== 'production' &&
        sortBySeries !== undefined &&
        !allSeries.includes(sortBySeries) &&
        !warnedSortBySeries.has(sortBySeries)
      ) {
        warnedSortBySeries.add(sortBySeries);
        console.warn(
          `[hen-ui/ComboChart] sortBySeries="${sortBySeries}" is not in barSeries or lineSeries [${allSeries.map((k) => `"${k}"`).join(', ')}]. Falling back to sum-of-series ranking — check the key name.`,
        );
      }
    }, [sortBySeries, allSeries]);

    // Pre-render row sort. Delegates to the pure `sortComboChartData` helper
    // (exported for unit testing) so the algorithm can be exercised without
    // a Recharts/jsdom render pass.
    const sortedData = React.useMemo(
      () => sortComboChartData(data, allSeries, sortBy, sortBySeries),
      [data, allSeries, sortBy, sortBySeries],
    );

    // Validate domains: min must be <= max
    const leftYAxisDomain = React.useMemo(() => {
      if (!leftDomainSafe) return undefined;
      if (leftDomainSafe[1] < leftDomainSafe[0]) {
        const key = `left-${leftDomainSafe[0]}-${leftDomainSafe[1]}`;
        if (process.env.NODE_ENV !== 'production' && !warnedDomain.has(key)) {
          warnedDomain.add(key);
          console.warn(
            `[hen-ui/ComboChart] leftYAxisDomain max (${leftDomainSafe[1]}) < min (${leftDomainSafe[0]}). Falling back to auto-fit.`,
          );
        }
        return undefined;
      }
      return leftDomainSafe;
    }, [leftDomainSafe]);

    const rightYAxisDomain = React.useMemo(() => {
      if (!rightDomainSafe) return undefined;
      if (rightDomainSafe[1] < rightDomainSafe[0]) {
        const key = `right-${rightDomainSafe[0]}-${rightDomainSafe[1]}`;
        if (process.env.NODE_ENV !== 'production' && !warnedDomain.has(key)) {
          warnedDomain.add(key);
          console.warn(
            `[hen-ui/ComboChart] rightYAxisDomain max (${rightDomainSafe[1]}) < min (${rightDomainSafe[0]}). Falling back to auto-fit.`,
          );
        }
        return undefined;
      }
      return rightDomainSafe;
    }, [rightDomainSafe]);

    // Dev-only guard: warn for series keys not present in data columns.
    // Uses `useEffect` (not `useMemo`) because this is a side-effect-only
    // hook — `useMemo` may run more than once per render and would also fire
    // during SSR. Matches the `sortBySeries` warning pattern above.
    React.useEffect(() => {
      if (process.env.NODE_ENV === 'production' || data.length === 0) return;
      const firstRow = data[0];
      for (const key of allSeries) {
        if (!(key in firstRow) && !warnedMissing.has(key)) {
          warnedMissing.add(key);
          console.warn(
            `[hen-ui/ComboChart] Series key "${key}" not found in data columns. It will be silently dropped from the render.`,
          );
        }
      }
    }, [data, allSeries]);

    // Sanitize series keys into CSS-custom-property-safe tokens. Human-readable
    // column names like "Fall 2026" produce `--color-Fall 2026` — an invalid CSS
    // declaration that the browser drops, leaving bars/lines black. `dataKey` on
    // each <Bar>/<Line> keeps the raw key so Recharts reads the correct data column;
    // `name`, config keys, and `var(--color-*)` references all use the safe token.
    const instanceId = React.useId();
    const safeKeyMap = React.useMemo(
      () => buildSafeSeriesKeyMap(allSeries),
      [allSeries],
    );
    const safeBarSeries = React.useMemo(
      () => barSeries.map((k) => safeKeyMap.get(k) ?? k),
      [barSeries, safeKeyMap],
    );
    const safeLineSeries = React.useMemo(
      () => lineSeries.map((k) => safeKeyMap.get(k) ?? k),
      [lineSeries, safeKeyMap],
    );
    const safeAllSeries = React.useMemo(
      () => [...safeBarSeries, ...safeLineSeries],
      [safeBarSeries, safeLineSeries],
    );
    const safeConfig = React.useMemo<ChartConfig>(() => {
      const next: ChartConfig = {};
      for (const rawKey of allSeries) {
        const safeKey = safeKeyMap.get(rawKey) ?? rawKey;
        const entry = config[rawKey];
        if (entry) {
          next[safeKey] =
            safeKey !== rawKey
              ? { ...entry, label: entry.label ?? rawKey }
              : entry;
        } else if (safeKey !== rawKey) {
          next[safeKey] = { label: rawKey };
        }
      }
      return next;
    }, [allSeries, config, safeKeyMap]);
    const safeSeriesColorConfigs = React.useMemo<
      Record<string, SeriesColorConfig> | undefined
    >(() => {
      if (!seriesColorConfigs) return undefined;
      const next: Record<string, SeriesColorConfig> = {};
      for (const [rawKey, val] of Object.entries(seriesColorConfigs)) {
        next[safeKeyMap.get(rawKey) ?? rawKey] = val;
      }
      return next;
    }, [seriesColorConfigs, safeKeyMap]);

    useMissingChartConfigWarning('ComboChart', config, allSeries);

    // Interactive legend toggle state (shared with bars + lines). Declared
    // before `resolvedConfig` so the sequential gradient spread can exclude
    // hidden series.
    const { hiddenSeries, handleSeriesToggle } = useInteractiveLegend({
      series: safeAllSeries,
      showLegend,
      clickableLegend,
      chartName: 'ComboChart',
    });

    // Auto-fill missing per-series colors based on `colorScale`. Categorical
    // (default) is byte-identical to historical behavior. Sequential spreads a
    // gradient across the visible bar + line series; diverging seeds the
    // high-arm base color (per-cell / per-dot sign mapping applied below).
    // Explicit `config[key].color` overrides still win.
    const resolvedConfig = React.useMemo(
      () =>
        withSeriesColorScaleDefaults(safeConfig, safeAllSeries, {
          colorScale,
          sequentialPalette,
          divergingPalette,
          hiddenSeries,
        }),
      [
        safeConfig,
        safeAllSeries,
        colorScale,
        sequentialPalette,
        divergingPalette,
        hiddenSeries,
      ],
    );

    const isDiverging = colorScale === 'diverging';

    // Mirror per-series `semanticColor` overrides into the config the
    // `<ChartLegend>` reads from, so the legend swatch matches the rendered
    // bar / line color. Series with only `colorRules` (no semanticColor)
    // intentionally keep the base palette color in the legend.
    const legendConfig = React.useMemo(
      () =>
        withSemanticLegendColors(
          resolvedConfig,
          safeSeriesColorConfigs,
          safeAllSeries,
        ),
      [resolvedConfig, safeSeriesColorConfigs, safeAllSeries],
    );

    const isStacked = mode === 'stacked';

    // Auto-derive left (bar) axis defaults
    const leftAxisDefaults = React.useMemo(() => {
      if (leftYAxisDomain) return undefined;
      if (barSeries.length === 0) return undefined;

      const cellValues = sortedData.flatMap((row) =>
        barSeries.map((key) =>
          typeof row[key] === 'number' ? (row[key] as number) : 0,
        ),
      );
      const stackTotals = isStacked
        ? sortedData.map((row) =>
            barSeries.reduce(
              (sum, key) =>
                sum + (typeof row[key] === 'number' ? (row[key] as number) : 0),
              0,
            ),
          )
        : [];
      const referenceHits = (referenceLines ?? [])
        .filter((rl) => (rl.axis ?? 'value') === 'value')
        .map((rl) => Number(rl.at))
        .filter(Number.isFinite);

      return deriveValueAxisDefaults([
        ...(isStacked ? stackTotals : cellValues),
        ...referenceHits,
      ]);
    }, [sortedData, barSeries, isStacked, referenceLines, leftYAxisDomain]);

    // Auto-derive right (line) axis defaults
    const rightAxisDefaults = React.useMemo(() => {
      if (rightYAxisDomain) return undefined;
      if (lineSeries.length === 0) return undefined;

      const cellValues = sortedData.flatMap((row) =>
        lineSeries.map((key) =>
          typeof row[key] === 'number' ? (row[key] as number) : NaN,
        ),
      );

      return deriveValueAxisDefaults(cellValues);
    }, [sortedData, lineSeries, rightYAxisDomain]);

    const resolvedLeftDomain =
      leftYAxisDomain ?? leftAxisDefaults?.domain ?? VALUE_AXIS_DEFAULT_DOMAIN;
    const resolvedLeftTicks = leftTicksSafe ?? leftAxisDefaults?.ticks;
    const resolvedRightDomain =
      rightYAxisDomain ??
      rightAxisDefaults?.domain ??
      VALUE_AXIS_DEFAULT_DOMAIN;
    const resolvedRightTicks = rightTicksSafe ?? rightAxisDefaults?.ticks;

    // Tooltip-mode resolution + pinned-tooltip plumbing. Hooks must run
    // before the early returns below to satisfy the rules of hooks.
    const resolvedTooltipMode = validateTooltipMode(tooltipMode);
    const {
      tooltipActive,
      onChartClick,
      containerRef: pinnedRef,
    } = usePinnedTooltip(resolvedTooltipMode);

    const needsRotation = shouldRotateXAxisLabels(sortedData, xAxisKey);
    const xAxisHeight = needsRotation
      ? getDynamicXAxisHeight(sortedData, xAxisKey, !!xAxisLabel)
      : xAxisLabel
        ? 50
        : 30;

    const sizingStyle: React.CSSProperties = fillContainer
      ? { minHeight: height }
      : { height };
    const sizingClass = fillContainer ? 'h-full' : undefined;

    const hasBarSeries = barSeries.length > 0;
    const hasLineSeries = lineSeries.length > 0;

    // Bind axes / grid against currently-visible series, not just declared
    // series. When the user hides every bar via the legend, no `<Bar>`
    // consumes `yAxisId='left'` anymore, so Recharts skips computing a
    // scale for that axis — `<CartesianGrid yAxisId='left'>` then has no
    // tick coordinates to draw against and the Y gridlines disappear.
    // Tracking visible series lets the grid (and tick rendering) fall
    // back to the still-active axis automatically. Single memo so we
    // only walk `hiddenSeries` once per render.
    const { hasVisibleBars, hasVisibleLines } = React.useMemo(
      () => ({
        hasVisibleBars: safeBarSeries.some((key) => !hiddenSeries.has(key)),
        hasVisibleLines: safeLineSeries.some((key) => !hiddenSeries.has(key)),
      }),
      [safeBarSeries, safeLineSeries, hiddenSeries],
    );

    // Per-axis tooltip formatter: look up whether the series name belongs to
    // bars (left axis) or lines (right axis) to pick the correct format.
    // Declared before the early returns so the hook runs on every render
    // (rules of hooks).
    const resolvedTooltipFormat = React.useMemo(() => {
      if (tooltipValueFormat) return tooltipValueFormat;
      if (!leftYAxisFormat && !rightYAxisFormat) return undefined;

      const barSet = new Set(safeBarSeries);
      return (value: number, name: string | number) => {
        const nameStr = String(name);
        if (barSet.has(nameStr) && leftYAxisFormat) {
          return leftYAxisFormat(value);
        }
        if (rightYAxisFormat) {
          return rightYAxisFormat(value);
        }
        if (leftYAxisFormat) {
          return leftYAxisFormat(value);
        }
        return String(value);
      };
    }, [tooltipValueFormat, leftYAxisFormat, rightYAxisFormat, safeBarSeries]);

    const handleRef = React.useCallback(
      (el: HTMLDivElement | null) => {
        pinnedRef.current = el;
        if (typeof ref === 'function') {
          ref(el);
        } else if (ref && 'current' in ref) {
          (ref as React.MutableRefObject<HTMLDivElement | null>).current = el;
        }
      },
      [pinnedRef, ref],
    );

    if (!hasBarSeries && !hasLineSeries) {
      return (
        <div
          ref={ref}
          aria-label={NO_SERIES_TEXT}
          className={cn(
            'flex items-center justify-center rounded bg-neutral-50 text-neutral-500 text-b4',
            sizingClass,
          )}
          style={sizingStyle}
        >
          {NO_SERIES_TEXT}
        </div>
      );
    }

    if (!data.length) {
      return (
        <div
          ref={ref}
          aria-label={EMPTY_FALLBACK_TEXT}
          className={cn(
            'flex items-center justify-center rounded bg-neutral-50 text-neutral-500 text-b4',
            sizingClass,
          )}
          style={sizingStyle}
        >
          {EMPTY_FALLBACK_TEXT}
        </div>
      );
    }

    const barRadius4: [number, number, number, number] = [
      barRadius,
      barRadius,
      0,
      0,
    ];

    // Pre-compute whether any *visible* bar series has active per-point
    // color rules — short-circuits the per-`<Cell>` map below for the
    // common (no rules) case. Per-series gating happens inline so a
    // series with only `semanticColor` (no `colorRules`) does NOT
    // allocate `data.length` `<Cell>` nodes per render — its color
    // comes from `<Bar fill>` directly.

    const renderBars = () => {
      const visibleBars = barSeries
        .map((rawKey, i) => ({ rawKey, safeKey: safeBarSeries[i] }))
        .filter(({ safeKey }) => !hiddenSeries.has(safeKey));
      if (isStacked) {
        if (visibleBars.length === 0) {
          return null;
        }
        return (
          <BarStack stackId={`stack-${instanceId}`} radius={barRadius4}>
            {visibleBars.map(({ rawKey, safeKey }) => {
              const cfg = safeSeriesColorConfigs?.[safeKey];
              const perCellCfg =
                cfg &&
                (!!cfg.colorRules?.length ||
                  cfg.semanticColor === 'auto-by-value')
                  ? cfg
                  : null;
              const needsPerCell = perCellCfg !== null || isDiverging;
              return (
                <Bar
                  key={safeKey}
                  dataKey={rawKey}
                  name={safeKey}
                  yAxisId='left'
                  fill={resolveSeriesBaseColor(safeKey, safeSeriesColorConfigs)}
                  radius={0}
                >
                  {needsPerCell &&
                    sortedData.map((entry, index) => {
                      const value =
                        typeof entry[rawKey] === 'number'
                          ? (entry[rawKey] as number)
                          : null;
                      const defaultColor = isDiverging
                        ? (resolveDivergingArmColor(value, divergingPalette) ??
                          `var(--color-${safeKey})`)
                        : `var(--color-${safeKey})`;
                      return (
                        <Cell
                          key={index}
                          fill={evaluateColorRules(
                            cfg?.colorRules,
                            value,
                            cfg?.semanticColor,
                            defaultColor,
                          )}
                        />
                      );
                    })}
                </Bar>
              );
            })}
          </BarStack>
        );
      }
      return visibleBars.map(({ rawKey, safeKey }) => {
        const cfg = safeSeriesColorConfigs?.[safeKey];
        // See stacked branch above for gating rationale.
        const perCellCfg =
          cfg &&
          (!!cfg.colorRules?.length || cfg.semanticColor === 'auto-by-value')
            ? cfg
            : null;
        const needsPerCell = perCellCfg !== null || isDiverging;
        return (
          <Bar
            key={safeKey}
            dataKey={rawKey}
            name={safeKey}
            yAxisId='left'
            fill={resolveSeriesBaseColor(safeKey, safeSeriesColorConfigs)}
            radius={barRadius4}
          >
            {needsPerCell &&
              sortedData.map((entry, index) => {
                const value =
                  typeof entry[rawKey] === 'number'
                    ? (entry[rawKey] as number)
                    : null;
                const defaultColor = isDiverging
                  ? (resolveDivergingArmColor(value, divergingPalette) ??
                    `var(--color-${safeKey})`)
                  : `var(--color-${safeKey})`;
                return (
                  <Cell
                    key={index}
                    fill={evaluateColorRules(
                      cfg?.colorRules,
                      value,
                      cfg?.semanticColor,
                      defaultColor,
                    )}
                  />
                );
              })}
          </Bar>
        );
      });
    };

    return (
      <div
        ref={handleRef}
        role='figure'
        aria-label='Combo chart'
        className={sizingClass}
        style={sizingStyle}
      >
        <ChartContainer
          config={resolvedConfig}
          className='aspect-auto h-full w-full'
        >
          <ComposedChart
            data={sortedData}
            maxBarSize={maxBarSize}
            accessibilityLayer
            onClick={onChartClick}
            margin={{
              right: getXAxisRightMargin(sortedData, xAxisKey, needsRotation),
              bottom: !showLegend && xAxisLabel ? X_AXIS_LABEL_MARGIN_PX : 0,
            }}
          >
            {/* Bind the grid explicitly to whichever value axis is the
                primary one. Without `yAxisId`, Recharts falls back to the
                default id `0` — which neither of our axes uses — and the
                grid silently snaps to the last-registered axis (here the
                right/line axis), leaving the left/bar axis (and its `y=0`
                baseline) with no horizontal gridlines. Bars own the
                baseline semantics, so prefer the left axis whenever bar
                series are *currently visible* and fall back to the right
                axis when every bar has been hidden via the legend (or
                for line-only combo charts). Using the *visible* flag —
                not the declared `hasBarSeries` — keeps Y gridlines from
                disappearing when the user toggles off all bars: an axis
                with no consumers has no computed scale, so a grid bound
                to it produces zero horizontal lines. */}
            <CartesianGrid
              strokeDasharray='3 3'
              yAxisId={hasVisibleBars ? 'left' : 'right'}
            />
            <XAxis
              dataKey={xAxisKey}
              height={xAxisHeight}
              interval={0}
              tick={(p) =>
                renderChartTick({
                  ...p,
                  rotate: needsRotation,
                  textAnchor: needsRotation ? 'end' : 'middle',
                  maxLabelLength: xAxisLabelMaxLength,
                })
              }
              label={
                xAxisLabel
                  ? { value: xAxisLabel, position: 'insideBottom', offset: -5 }
                  : undefined
              }
              tickLine={false}
              axisLine={false}
            />

            {/* Left Y-axis (bars) — hidden when no bar series are
                visible (declared OR currently toggled-on via legend). */}
            <YAxis
              yAxisId='left'
              domain={resolvedLeftDomain}
              ticks={resolvedLeftTicks}
              tickFormatter={leftYAxisFormat}
              label={
                leftYAxisLabel
                  ? {
                      value: leftYAxisLabel,
                      angle: -90,
                      position: 'insideLeft',
                      style: { textAnchor: 'middle' },
                    }
                  : undefined
              }
              tick={hasVisibleBars ? { fontSize: 12 } : false}
              tickLine={false}
              axisLine={false}
              hide={!hasVisibleBars}
            />

            {/* Right Y-axis (lines) — hidden when no line series are
                visible. */}
            <YAxis
              yAxisId='right'
              orientation='right'
              domain={resolvedRightDomain}
              ticks={resolvedRightTicks}
              tickFormatter={rightYAxisFormat}
              label={
                rightYAxisLabel
                  ? {
                      value: rightYAxisLabel,
                      angle: 90,
                      position: 'insideRight',
                      style: { textAnchor: 'middle' },
                    }
                  : undefined
              }
              tick={hasVisibleLines ? { fontSize: 12 } : false}
              tickLine={false}
              axisLine={false}
              hide={!hasVisibleLines}
            />

            {resolvedTooltipMode !== 'disabled' && (
              <ChartTooltip
                cursor={false}
                {...(resolvedTooltipMode === 'pinned'
                  ? { active: tooltipActive, trigger: 'click' as const }
                  : {})}
                content={
                  <ChartTooltipContent
                    indicator='dot'
                    hideLabel={hideLabel}
                    valueFormatter={
                      tooltipFormatter ? undefined : resolvedTooltipFormat
                    }
                    rowValueFormatter={tooltipFormatter}
                    footer={renderTooltipFooter(tooltipContent)}
                    getItemIndicatorColor={buildTooltipIndicatorColorResolver(
                      safeSeriesColorConfigs,
                    )}
                  />
                }
              />
            )}
            {showLegend && (
              <ChartLegend
                series={safeAllSeries}
                config={legendConfig}
                seriesColorConfigs={safeSeriesColorConfigs}
                colorScale={colorScale}
                divergingPalette={divergingPalette}
                clickableLegend={clickableLegend}
                hiddenSeries={hiddenSeries}
                onSeriesToggle={handleSeriesToggle}
                verticalAlign='bottom'
                align='center'
                wrapperStyle={
                  xAxisLabel || needsRotation ? { paddingTop: 12 } : undefined
                }
              />
            )}

            {renderBars()}

            {lineSeries
              .map((rawKey, i) => ({ rawKey, safeKey: safeLineSeries[i] }))
              .filter(({ safeKey }) => !hiddenSeries.has(safeKey))
              .map(({ rawKey, safeKey }) => {
                const colorCfg = safeSeriesColorConfigs?.[safeKey];
                const baseStroke = resolveSeriesBaseColor(
                  safeKey,
                  safeSeriesColorConfigs,
                );
                // Install the per-point custom dot when EITHER:
                //   - actual `colorRules` are defined, OR
                //   - `semanticColor === 'auto-by-value'` — which has no
                //     single representative color, so each dot's stroke
                //     must be resolved per-value via `evaluateColorRules`.
                // With only a STATIC `semanticColor` (or no config), we
                // fall back to Recharts' default dot behavior: hollow
                // ring at rest, filled `activeDot` on hover.
                //
                // Carry `colorCfg` through the gate as a single narrowed
                // local so TypeScript knows it's non-null inside the
                // dot/activeDot closures below (avoids non-null
                // assertions on `colorRules` / `semanticColor`).
                const perPointCfg =
                  colorCfg &&
                  (!!colorCfg.colorRules?.length ||
                    colorCfg.semanticColor === 'auto-by-value')
                    ? colorCfg
                    : null;
                // `diverging` colors each dot by its value's sign, so it needs
                // the per-point dot path even without `seriesColorConfigs`.
                const needsPerPoint = perPointCfg !== null || isDiverging;
                const resolveDotColor = (val: number | null): string => {
                  const defaultColor = isDiverging
                    ? (resolveDivergingArmColor(val, divergingPalette) ??
                      baseStroke)
                    : baseStroke;
                  // Evaluate the raw `colorCfg` (not the perf-gated
                  // `perPointCfg`) so a static `semanticColor` still wins over
                  // the diverging default when diverging forces the dot path.
                  return evaluateColorRules(
                    colorCfg?.colorRules,
                    val,
                    colorCfg?.semanticColor,
                    defaultColor,
                  );
                };

                const customDot =
                  needsPerPoint && showDots
                    ? (props: Record<string, unknown>) => {
                        const { cx, cy, payload } = props as {
                          cx: number;
                          cy: number;
                          payload: Record<string, unknown>;
                        };
                        const val =
                          typeof payload[rawKey] === 'number'
                            ? (payload[rawKey] as number)
                            : null;
                        const strokeColor = resolveDotColor(val);
                        return (
                          <circle
                            cx={cx}
                            cy={cy}
                            r={3}
                            fill='var(--neutral-white, #FFFFFF)'
                            stroke={strokeColor}
                            strokeWidth={1.5}
                            data-hen-rule-dot=''
                          />
                        );
                      }
                    : undefined;

                const customActiveDot = needsPerPoint
                  ? (props: Record<string, unknown>) => {
                      const { cx, cy, payload } = props as {
                        cx: number;
                        cy: number;
                        payload: Record<string, unknown>;
                      };
                      const val =
                        typeof payload[rawKey] === 'number'
                          ? (payload[rawKey] as number)
                          : null;
                      const fill = resolveDotColor(val);
                      return (
                        <circle
                          cx={cx}
                          cy={cy}
                          r={5}
                          fill={fill}
                          stroke={fill}
                          strokeWidth={0}
                        />
                      );
                    }
                  : undefined;

                return (
                  <Line
                    key={safeKey}
                    type={curveType}
                    dataKey={rawKey}
                    name={safeKey}
                    yAxisId='right'
                    stroke={baseStroke}
                    strokeWidth={2}
                    dot={customDot ?? showDots}
                    connectNulls={connectNulls}
                    activeDot={
                      (customActiveDot ?? { r: 5 }) as React.ComponentProps<
                        typeof Line
                      >['activeDot']
                    }
                  />
                );
              })}

            {renderReferenceLines(referenceLines, 'vertical')}
          </ComposedChart>
        </ChartContainer>
      </div>
    );
  },
);
ComboChart.displayName = 'ComboChart';
